import { useEffect, useRef, useState, ReactNode } from 'react'

interface ResizablePanelProps {
  children: [ReactNode, ReactNode]
  defaultLeftWidth?: number
  minLeftWidth?: number
  maxLeftWidth?: number
  storageKey?: string
  className?: string
}

export default function ResizablePanel({
  children,
  defaultLeftWidth = 60,
  minLeftWidth = 30,
  maxLeftWidth = 80,
  storageKey = 'resizable-panel-width',
  className = ''
}: ResizablePanelProps) {
  const [leftWidth, setLeftWidth] = useState<number>(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = parseFloat(saved)
        if (parsed >= minLeftWidth && parsed <= maxLeftWidth) {
          return parsed
        }
      }
    }
    return defaultLeftWidth
  })

  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, leftWidth.toString())
    }
  }, [leftWidth, storageKey])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return

      const container = containerRef.current
      const containerRect = container.getBoundingClientRect()
      const containerWidth = containerRect.width

      const newLeftWidth = ((e.clientX - containerRect.left) / containerWidth) * 100

      if (newLeftWidth >= minLeftWidth && newLeftWidth <= maxLeftWidth) {
        setLeftWidth(newLeftWidth)
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, minLeftWidth, maxLeftWidth])

  const dividerWidth = 2

  return (
    <div 
      ref={containerRef} 
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Left Panel - Absolute positioned */}
      <div
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: `calc(${leftWidth}% - ${dividerWidth / 2}px)`,
          overflow: 'hidden'
        }}
      >
        {children[0]}
      </div>

      {/* Divider - Absolute positioned */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `calc(${leftWidth}% - ${dividerWidth / 2}px)`,
          width: `${dividerWidth}px`,
          cursor: 'col-resize',
          backgroundColor: isDragging ? 'var(--color-primary)' : 'var(--color-line)',
          transition: 'background-color 0.15s',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'var(--color-primary)'
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'var(--color-line)'
          }
        }}
      />

      {/* Right Panel - Absolute positioned */}
      <div
        style={{ 
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: `calc(${100 - leftWidth}% - ${dividerWidth / 2}px)`,
          overflow: 'hidden'
        }}
      >
        {children[1]}
      </div>
    </div>
  )
}
